/**
 * PortalLayout - Customer-facing portal layout
 * Minimal, mobile-first navigation for customers
 */

import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Calendar, Car, LogOut, Menu, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { getApiBase } from '../../lib/apiBase';

const API_BASE = getApiBase();

const navItems = [
  { path: '/portal/bookings', label: 'Mis Citas', icon: Calendar },
  { path: '/portal/vehicles', label: 'Mis Vehículos', icon: Car },
];

export function PortalLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [customerName, setCustomerName] = useState<string>('');

  useEffect(() => {
    // Check session
    const sessionId = localStorage.getItem('customer_session');
    if (!sessionId) {
      navigate('/portal/login', { replace: true });
      return;
    }

    // Fetch customer info
    fetch(`${API_BASE}/api/customer-portal/vehicles`, {
      headers: { 'X-Customer-Session': sessionId },
    })
      .then((res) => {
        if (!res.ok) {
          localStorage.removeItem('customer_session');
          navigate('/portal/login', { replace: true });
        }
      })
      .catch(() => {
        // Don't redirect on network error
      });

    const name = localStorage.getItem('customer_name');
    if (name) setCustomerName(name);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('customer_session');
    localStorage.removeItem('customer_name');
    navigate('/portal/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-1.5 rounded-md hover:bg-gray-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <Link to="/portal/bookings" className="font-semibold text-gray-900 text-lg">
              Mi Portal
            </Link>
          </div>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              const isActive = location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
            <div className="ml-2 pl-2 border-l border-gray-200">
              {customerName && (
                <span className="text-sm text-gray-500 mr-2">{customerName}</span>
              )}
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-500">
                <LogOut className="h-4 w-4 mr-1" />
                Salir
              </Button>
            </div>
          </nav>
        </div>

        {/* Mobile nav */}
        {mobileMenuOpen && (
          <nav className="lg:hidden border-t border-gray-100 bg-white px-4 py-2 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium ${
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-red-600 hover:bg-red-50 w-full"
            >
              <LogOut className="h-4 w-4" />
              Cerrar Sesión
            </button>
          </nav>
        )}
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto">
        <Outlet />
      </main>
    </div>
  );
}
